import Box from "@mui/material/Box"
import Popover from "@mui/material/Popover"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { format as formatDate } from "date-fns"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import BorderedGrayChip from "../components/BorderedGrayChip"
import { getCandidateTypeLabel } from "./candidateType"
import { getEventCandidateName } from "./utils"

export default function EventPopover({ anchorElem, onClose, eventTarget }) {
  if (!eventTarget) {
    return null
  }

  let eventDateStrPropName
  switch (eventTarget.type) {
    case EVENT_TYPES.bookingLog:
      eventDateStrPropName = "stamp"
      break
    case EVENT_TYPES.meetingSchedule:
      eventDateStrPropName = "start_datetime"
      break
    default:
      eventDateStrPropName = "created"
  }

  let title
  switch (eventTarget.type) {
    case EVENT_TYPES.bookingLog:
      title = getCandidateTypeLabel(eventTarget) || "Meeting"
      break
    case EVENT_TYPES.meetingSchedule:
      title = "Meeting"
      break
    case EVENT_TYPES.call:
      title = "Call"
      break
    default:
      title = "Email"
  }

  let notes
  switch (eventTarget.type) {
    case EVENT_TYPES.bookingLog:
      notes = eventTarget.notes
      break
    case EVENT_TYPES.meetingSchedule:
      notes = eventTarget.description
      break
    default:
      notes = ""
  }

  const eventDate = new Date(eventTarget[eventDateStrPropName])
  const longMonth = formatDate(eventDate, "MMMM")
  const day = formatDate(eventDate, "d")

  return (
    <Popover
      anchorEl={anchorElem}
      open={Boolean(anchorElem)}
      onClose={onClose}
      elevation={1}
      transformOrigin={{
        horizontal: "left",
        vertical: "center",
      }}
    >
      <Container>
        <PurpleLabelBox />
        <EventTypeChip label={title} />
        <Column sx={{ p: 4, mt: 1, flex: 1, minHeight: 0 }}>
          <Row sx={{ alignItems: "center", gap: 2 }}>
            <DateBox>
              <DateBoxMonth>{longMonth}</DateBoxMonth>
              <DateBoxDay>{day}</DateBoxDay>
            </DateBox>
            <Column>
              <Typography variant="h2">
                {getEventCandidateName(eventTarget)}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {formatDate(eventDate, "hh:mm aaa")}
              </Typography>
            </Column>
          </Row>
          <Column sx={{ mt: 2, flex: 1, minHeight: 0 }}>
            <Typography variant="h5" fontWeight={700}>
              Note
            </Typography>
            <NotesTypography>{notes?.trim() ? notes : "--"}</NotesTypography>
          </Column>
        </Column>
        <Row
          sx={{
            px: 4,
            py: 3,
            alignItems: "center",
            backgroundColor: (theme) => theme.palette.colors.gray50,
          }}
        >
          <Column>
            <Typography variant="h5" fontWeight={700}>
              Date of event
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {formatDate(eventDate, "EEEE, dd MMMM yyyy")}
            </Typography>
          </Column>
        </Row>
      </Container>
    </Popover>
  )
}

const Container = styled(Column)(() => ({
  justifyContent: "space-between",
  width: "360px",
  maxHeight: "480px",
  borderRadius: "16px",
  position: "relative",
}))

const NotesTypography = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowY: "auto",
  maxHeight: "220px",
  fontSize: "14px",
}))

const PurpleLabelBox = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  background:
    "linear-gradient(315deg, rgba(0, 0, 0, 0) 50%, #9525D6 50%, #9525D6 100%)",
  width: "30px",
  height: "30px",
}))

const EventTypeChip = styled(BorderedGrayChip)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
}))

const DateBox = styled(Column)(() => ({
  width: "62px",
  height: "62px",
  borderRadius: "8px",
  boxShadow: "0px 12px 16px 0px #A9C2D126",
}))

const DateBoxMonth = styled(Row)(({ theme }) => ({
  justifyContent: "center",
  alignItems: "center",
  height: "19px",
  fontSize: "8px",
  fontWeight: "500",
  color: "white",
  backgroundColor: theme.palette.colors.gray900,
  borderRadius: "8px 8px 0 0",
}))

const DateBoxDay = styled(Column)(({ theme }) => ({
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  fontSize: "30px",
  fontWeight: 700,
  color: theme.palette.colors.gray700,
}))

export const EVENT_TYPES = {
  bookingLog: "booking_log",
  meetingSchedule: "meeting_schedule",
  call: "call",
  emailSendEvent: "email_send_event",
}
